import React, { FC } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';

const Cookies: FC = () => {
  const navigate = useNavigate();

  const handleNonFunctionalCTA = () => {
    navigate('/contact');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-teaGreen/5 via-white to-primary-lightGreen/10">
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-24 md:pb-20 overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-teaGreen/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-lightGreen/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-black mb-6 leading-tight">
              Cookie Policy
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              Learn about how we use cookies and similar technologies to enhance your experience.
            </p>
            <p className="text-sm text-gray-500">
              Last updated: January 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="relative pb-20 md:pb-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100">
            
            {/* Introduction */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-4">What Are Cookies?</h2>
              <p className="text-gray-700 leading-relaxed">
                Cookies are small text files that are stored on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our platform.
              </p>
            </div>

            {/* Types of Cookies */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-4">Types of Cookies We Use</h2>
              
              <div className="space-y-6">
                {/* Essential Cookies */}
                <div className="bg-primary-teaGreen/10 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-primary-black mb-3">Essential Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies are necessary for the website to function properly and cannot be disabled.
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Authentication and login status</li>
                    <li>Security and fraud prevention</li>
                    <li>Basic website functionality</li>
                    <li>Form submissions and preferences</li>
                  </ul>
                </div>

                {/* Performance Cookies */}
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-primary-black mb-3">Performance Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies help us understand how visitors interact with our website.
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Page views and navigation patterns</li>
                    <li>Time spent on pages</li>
                    <li>Error tracking and debugging</li>
                    <li>Website performance metrics</li>
                  </ul>
                </div>

                {/* Functional Cookies */}
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-primary-black mb-3">Functional Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies enable enhanced functionality and personalization.
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Language and region preferences</li>
                    <li>Dark/light mode settings</li>
                    <li>Job search filters and preferences</li>
                    <li>User interface customizations</li>
                  </ul>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-purple-50 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-primary-black mb-3">Marketing Cookies</h3>
                  <p className="text-gray-700 mb-3">
                    These cookies are used to deliver relevant advertisements and track campaign effectiveness.
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Ad targeting and personalization</li>
                    <li>Campaign performance tracking</li>
                    <li>Social media integration</li>
                    <li>Retargeting and remarketing</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Third-Party Cookies */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-4">Third-Party Cookies</h2>
              <p className="text-gray-700 mb-4">
                We use third-party services that may set their own cookies:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Razorpay:</strong> Payment processing and security</li>
                <li><strong>Google Analytics:</strong> Website analytics and performance tracking</li>
                <li><strong>Social Media Platforms:</strong> Social sharing and login functionality</li>
                <li><strong>Recaptcha:</strong> Bot protection and security</li>
                <li><strong>CDN Services:</strong> Content delivery and performance optimization</li>
              </ul>
            </div>

            {/* Cookie Duration */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-4">Cookie Duration</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 px-4 py-2 text-left">Cookie Type</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Duration</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Session Cookies</td>
                      <td className="border border-gray-300 px-4 py-2">Until browser closes</td>
                      <td className="border border-gray-300 px-4 py-2">Temporary functionality</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Authentication</td>
                      <td className="border border-gray-300 px-4 py-2">30 days</td>
                      <td className="border border-gray-300 px-4 py-2">Login status</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Preferences</td>
                      <td className="border border-gray-300 px-4 py-2">1 year</td>
                      <td className="border border-gray-300 px-4 py-2">User settings</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2">Analytics</td>
                      <td className="border border-gray-300 px-4 py-2">2 years</td>
                      <td className="border border-gray-300 px-4 py-2">Usage tracking</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Managing Cookies */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-4">Managing Your Cookie Preferences</h2>
              
              <h3 className="text-xl font-semibold text-primary-black mb-3">Browser Settings</h3>
              <p className="text-gray-700 mb-4">
                You can control cookies through your browser settings. Here's how to manage cookies in popular browsers:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Cookies and site permissions → Cookies and site data</li>
              </ul>

              <h3 className="text-xl font-semibold text-primary-black mb-3 mt-6">Cookie Consent</h3>
              <p className="text-gray-700 mb-4">
                When you first visit our website, you'll see a cookie consent banner. You can:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Accept all cookies for the best experience</li>
                <li>Customize your preferences by category</li>
                <li>Reject non-essential cookies</li>
                <li>Change your preferences anytime in your account settings</li>
              </ul>
            </div>

            {/* Impact of Disabling Cookies */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-4">Impact of Disabling Cookies</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-3">⚠️ Important Notice</h3>
                <p className="text-gray-700 mb-4">
                  Disabling certain cookies may affect your experience on our platform:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>You may need to log in repeatedly</li>
                  <li>Your preferences won't be saved</li>
                  <li>Some features may not work properly</li>
                  <li>Job recommendations may be less accurate</li>
                  <li>Website performance may be affected</li>
                </ul>
              </div>
            </div>

            {/* Mobile Apps */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-4">Mobile Applications</h2>
              <p className="text-gray-700 leading-relaxed">
                Our mobile applications may use similar technologies to cookies, such as device identifiers and local storage. These serve the same purposes as cookies and can be managed through your device settings or app preferences.
              </p>
            </div>

            {/* Updates to Policy */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-4">Updates to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the updated policy on our website.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-4">Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-primary-teaGreen/10 rounded-xl p-6">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> operations@dhoondh.in
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Phone:</strong> +91 93168 46322
                </p>
                <p className="text-gray-700">
                  <strong>Address:</strong>
                    510, Serene Centrum
                    Laxmipura, Vadodara – 390021
                </p>
              </div>
            </div>

            {/* Cookie Settings Button */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-primary-black mb-4">Manage Your Cookie Preferences</h2>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={handleNonFunctionalCTA}
                >
                  Cookie Settings
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={handleNonFunctionalCTA}
                >
                  Clear Non-Essential Cookies
                </Button>
              </div>
            </div>

            {/* Back to Home */}
            <div className="text-center pt-8 border-t border-gray-200">
              <Link to="/">
                <Button variant="outline" size="lg">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cookies;
